// Coglative Fleet dashboard SPA. esbuild compiles this JSX at build time (React + ReactDOM bundled
// from pnpm deps), so the page needs neither Babel-standalone nor the unsafe-eval CSP it required.
// window.PanelRegistry / BridgeOp / getInstalledPanels / WasmPanel are still classic <script src>
// globals loaded by index.html.

import React from 'react';
import { createRoot } from 'react-dom/client';

// Static import (not the former dynamic one): esbuild bundles + tree-shakes it into app.js.
import { discoverPanels, PANEL_PREDICATE_URI } from './panel-discovery.js';

// Re-expose React for classic-script consumers not yet on ES modules — wasm-panel.js is the bridge
// between the host React tree and the panel WASM exports, and index.html no longer loads React.
window.React = React;

    // ═══════════════════════════════════════════════════════════════
    // Icons
    // ═══════════════════════════════════════════════════════════════
    function Ic({d, size = 20, s = 1.5}) {
        return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={s}
                    strokeLinecap="round" strokeLinejoin="round">{d}</svg>
    }

    const ICONS = {
        home: <Ic d={<path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z"/>}/>,
        rooms: <Ic d={<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>}/>,
        tasks: <Ic d={<>
            <rect x="4" y="5" width="16" height="16" rx="2"/>
            <path d="m9 12 2 2 4-4"/>
            <path d="M9 3h6v4H9z"/>
        </>}/>,
        fleet: <Ic d={<>
            <rect x="5" y="7" width="14" height="12" rx="3"/>
            <path d="M12 3v4M9 12h.01M15 12h.01M9 16h6"/>
        </>}/>,
        pulse: <Ic d={<path d="M13 2 4 14h7l-1 8 9-12h-7z"/>}/>,
        more: <Ic d={<>
            <circle cx="5" cy="12" r="1.3" fill="currentColor"/>
            <circle cx="12" cy="12" r="1.3" fill="currentColor"/>
            <circle cx="19" cy="12" r="1.3" fill="currentColor"/>
        </>}/>,
        village: <Ic d={<>
            <circle cx="9" cy="8" r="3"/>
            <circle cx="17" cy="10" r="2"/>
            <path d="M3 20c0-3 3-5 6-5s6 2 6 5"/>
            <path d="M15 20c0-2 2-3 4-3"/>
        </>}/>,
        metrics: <Ic d={<path d="M3 5h18l-7 9v5l-4 2v-7z"/>}/>,
        finance: <Ic d={<>
            <rect x="3" y="6" width="18" height="13" rx="2"/>
            <path d="M3 10h18M7 15h3"/>
        </>}/>,
        iam: <Ic d={<>
            <path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6z"/>
            <path d="m9 12 2 2 4-4"/>
        </>}/>,
        entities: <Ic d={<path d="m8 8-5 4 5 4M16 8l5 4-5 4M14 4l-4 16"/>}/>,
        invest: <Ic d={<>
            <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6"/>
        </>}/>,
        clusters: <Ic d={<>
            <circle cx="12" cy="12" r="9"/>
            <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/>
        </>}/>,
        back: <Ic d={<path d="m15 6-6 6 6 6"/>}/>,
        settings: <Ic d={<>
            <circle cx="12" cy="12" r="3"/>
            <path
                d={"M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 "
                  + "1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 "
                  + "1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 "
                  + "9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9A1.7 1.7 0 0 0 10 "
                  + "3.1V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 "
                  + "2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"}/>
        </>}/>,
    };

    // LazyPanel — discovery-only renderer. EVERY panel arrives via the iam:panel discovery chain as
    // kind:'wasm'; there is no static fallback, so broken discovery is VISIBLY broken (the forcing
    // function that #746 started).
    // mode + size props per SPEC-panel-render-protocol-v0 §"Manifest variants": the caller declares
    // its mount intent and LazyPanel cross-checks it against the panel's declared variants[].
    // A manifest with no variants[] is treated as implicit-FULL, so existing route-based mounts keep
    // working; once every manifest carries variants that case becomes a hard reject.
    function LazyPanel({id, principal, scope, onNavigate, mode, size}) {
        const installed = (typeof window.getInstalledPanels === 'function')
            ? window.getInstalledPanels()
            : (window.PanelRegistry || []);
        const entry = installed.find(p => p.id === id);

        // Without WebTransport the bridge never dials, so discovery can never deliver a panel.
        if (!entry && typeof WebTransport === 'undefined') {
            return <div className="loading" style={{color: 'var(--wn)'}}>
                This browser has no WebTransport, which the mesh needs. Use Safari 26 or later,
                a current Chrome or Firefox, or the iOS app.
            </div>;
        }

        if (!entry) {
            return <div className="loading" style={{color: 'var(--wn)'}}>
                No panel registered for id "{id}" — discovery hasn't delivered it yet,
                or the iam:panel manifest isn't published.
            </div>;
        }

        if (entry.kind !== 'wasm') {
            return <div className="loading" style={{color: 'var(--wn)'}}>
                Panel "{id}" has kind="{entry.kind || '(unset)'}" — only kind:"wasm" is
                supported after the 2026-05-14 eradication of static kind:"js" panels.
                Re-implement as a discovered WASM panel.
            </div>;
        }

        const requestedMode = mode || 'FULL';

        // If the manifest declares variants[], the requested mode MUST match a declared variant.mode.
        if (Array.isArray(entry.variants) && entry.variants.length > 0) {
            const variant = entry.variants.find(v => v && v.mode === requestedMode);
            if (!variant) {
                const declared = entry.variants.map(v => v && v.mode).filter(Boolean).join(', ');
                return <div className="loading" style={{color: 'var(--wn)'}}>
                    Panel "{id}" not declared for mode="{requestedMode}". Manifest variants:
                    [{declared || '(none)'}]. Caller must request a mode the panel supports
                    (per SPEC-panel-render-protocol-v0 §"Manifest variants").
                </div>;
            }
            // WIDGET: a caller-supplied size MUST match a declared sizes[] entry; no size = default.
            if (requestedMode === 'WIDGET' && Array.isArray(variant.sizes)) {
                if (size && !variant.sizes.find(s => s && s.x === size.x && s.y === size.y)) {
                    const declared = variant.sizes.map(s => `${s.x}x${s.y}`).join(', ');
                    return <div className="loading" style={{color: 'var(--wn)'}}>
                        Panel "{id}" size={size.x}x{size.y} not in declared WIDGET sizes
                        [{declared}]. Resize-snap should have aligned this; caller bug.
                    </div>;
                }
            }
            // FULL: the caller clamps; this is only a sanity check against minSize.
            if (requestedMode === 'FULL' && variant.minSize && size) {
                if (size.x < variant.minSize.x || size.y < variant.minSize.y) {
                    return <div className="loading" style={{color: 'var(--wn)'}}>
                        Panel "{id}" size={size.x}x{size.y} below declared FULL minSize=
                        {variant.minSize.x}x{variant.minSize.y}. Caller must clamp.
                    </div>;
                }
            }
        }

        const W = window.WasmPanel;
        if (!W) return <div className="loading" style={{color: 'var(--wn)'}}>WasmPanel not loaded</div>;
        return <W entry={entry} principal={principal} scope={scope} onNavigate={onNavigate}
                  mode={requestedMode} size={size} state={0}/>;
    }

    // ═══════════════════════════════════════════════════════════════
    // Context modal with blur-resolve-settle choreography
    // ═══════════════════════════════════════════════════════════════
    function ContextModal({open, onClose, onApply}) {
        const [stage, setStage] = React.useState('picking');
        const principals = ['brian', 'claude-infra', 'mcp-builder', 'guest'];
        const scopes = ['coglative', 'coglative.platform', 'coglative.platform.class-project',
            'coglative.fleet', 'coglative.class'];
        const [p, setP] = React.useState('brian');
        const [s, setS] = React.useState('coglative.platform');
        React.useEffect(() => {
            if (open) setStage('picking');
        }, [open]);
        if (!open) return null;

        function apply() {
            setStage('applying');
            setTimeout(() => {
                setStage('settling');
                onApply(p, s);
            }, 500);
            setTimeout(() => onClose(), 800);
        }

        return (
            <div style={{
                position: 'fixed',
                inset: 0,
                zIndex: 50,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center'
            }}>
                <div onClick={stage === 'picking' ? onClose : undefined} style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.55)',
                    backdropFilter: 'blur(2px)',
                    transition: 'opacity 260ms',
                    opacity: stage === 'settling' ? 0 : 1
                }}/>
                <div style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 480,
                    maxHeight: '82vh',
                    background: 'var(--bge)',
                    borderTopLeftRadius: 18,
                    borderTopRightRadius: 18,
                    border: '1px solid var(--ln)',
                    borderBottom: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    transition: 'transform 260ms cubic-bezier(.4,0,.2,1)',
                    transform: stage === 'settling' ? 'translateY(100%)' : 'translateY(0)'
                }}>
                    <div style={{padding: '8px 0 4px', display: 'flex', justifyContent: 'center'}}>
                        <div style={{width: 36, height: 4, background: 'var(--ln)', borderRadius: 2}}/>
                    </div>
                    <div style={{padding: '6px 18px 12px'}}>
                        <div style={{fontSize: 17, fontWeight: 700, letterSpacing: -0.3}}>Switch context</div>
                        <div style={{
                            fontSize: 11,
                            color: 'var(--txm)',
                            fontFamily: 'var(--mono)',
                            marginTop: 2
                        }}>{stage === 'applying' ? 'Resolving…' : 'Principal · scope'}</div>
                    </div>
                    <div className="cog-scroll" style={{flex: 1, overflow: 'auto', padding: '0 14px 14px'}}>
                        <div style={{
                            fontFamily: 'var(--mono)',
                            fontSize: 10,
                            color: 'var(--txd)',
                            letterSpacing: 1.5,
                            textTransform: 'uppercase',
                            marginBottom: 6
                        }}>Acting as
                        </div>
                        {principals.map(pr => (
                            <div key={pr} onClick={() => setP(pr)} style={{
                                padding: '10px 12px',
                                borderRadius: 10,
                                marginBottom: 6,
                                cursor: 'pointer',
                                background: p === pr ? 'var(--acd)' : 'var(--bgi)',
                                border: `1px solid ${p === pr ? 'var(--ac)' : 'var(--lns)'}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10
                            }}>
                                <div style={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: pr.includes('-') ? 6 : 13,
                                    background: pr.includes('-') ? 'var(--acd)' : 'var(--hud)',
                                    color: pr.includes('-') ? 'var(--ac)' : 'var(--hu)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    fontFamily: 'var(--mono)'
                                }}>{pr.includes('-') ? '◆' : pr[0].toUpperCase()}</div>
                                <span style={{fontSize: 13, fontWeight: 600}}>{pr}</span>
                                {p === pr && <span style={{marginLeft: 'auto', color: 'var(--ac)'}}>✓</span>}
                            </div>
                        ))}
                        <div style={{
                            fontFamily: 'var(--mono)',
                            fontSize: 10,
                            color: 'var(--txd)',
                            letterSpacing: 1.5,
                            textTransform: 'uppercase',
                            margin: '12px 0 6px'
                        }}>Scope
                        </div>
                        <div style={{
                            background: 'var(--bgi)',
                            borderRadius: 10,
                            padding: '8px 12px',
                            border: '1px solid var(--lns)',
                            fontFamily: 'var(--mono)',
                            fontSize: 11.5,
                            lineHeight: 1.8
                        }}>
                            {scopes.map(sc => (
                                <div key={sc} onClick={() => setS(sc)} style={{
                                    paddingLeft: (sc.split('.').length - 1) * 14,
                                    cursor: 'pointer',
                                    color: s === sc ? 'var(--ac)' : 'var(--txm)'
                                }}>
                                    {sc.split('.').pop()} {s === sc && <span>●</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div
                        style={{padding: '10px 16px 20px', borderTop: '1px solid var(--lns)', display: 'flex', gap: 8}}>
                        <button onClick={onClose} disabled={stage !== 'picking'} style={{
                            flex: 1,
                            padding: 11,
                            borderRadius: 10,
                            background: 'var(--bge2)',
                            border: '1px solid var(--ln)',
                            color: 'var(--tx)',
                            fontSize: 14
                        }}>Cancel
                        </button>
                        <button onClick={apply} disabled={stage !== 'picking'} style={{
                            flex: 2,
                            padding: 11,
                            borderRadius: 10,
                            background: 'var(--ac)',
                            border: 'none',
                            color: 'var(--bg)',
                            fontSize: 14,
                            fontWeight: 600,
                            opacity: stage !== 'picking' ? 0.5 : 1
                        }}>{stage === 'applying' ? 'Resolving…' : 'Apply'}</button>
                    </div>
                </div>
            </div>
        );
    }

    // Gate — the paywall / entry choice for a FRESH visitor (no cap-token, no device key, no anon
    // cookie). Returning and identified users never see it. Two doors: log in (OAuth identify), or
    // "try for free" (an anon EBA account on this device, upgradable later). Styling is
    // self-contained because the chrome's CSS vars are not guaranteed on this pre-app screen.
    function Gate({onLogin, onTryFree}) {
        const [busy, setBusy] = React.useState(false);
        const accent = 'rgb(79,143,247)';
        async function tryFree() {
            if (busy) return;
            setBusy(true);
            try {
                await onTryFree();
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[gate] try-for-free failed:', e && e.message ? e.message : e);
                setBusy(false);
            }
        }
        async function login() {
            if (busy) return;
            setBusy(true);
            try {
                await onLogin();
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[gate] log-in failed:', e && e.message ? e.message : e);
                setBusy(false);
            }
        }
        return (
            <div style={{
                position: 'fixed',
                inset: 0,
                background: 'rgb(13,17,23)',
                color: '#e6edf3',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
                fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
                textAlign: 'center'
            }}>
                <div style={{
                    fontSize: 22,
                    fontWeight: 700,
                    letterSpacing: -0.3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 28
                }}>
                    <span style={{color: accent}}>◆</span> Coglative
                </div>
                <div style={{fontSize: 28, fontWeight: 700, letterSpacing: -0.5, marginBottom: 10}}>
                    Welcome to Coglative
                </div>
                <div style={{
                    fontSize: 15,
                    color: 'rgba(255,255,255,0.55)',
                    maxWidth: 360,
                    lineHeight: 1.6,
                    marginBottom: 32
                }}>
                    Your mesh, your compute, your rooms. Pick how you'd like to start.
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                    width: '100%',
                    maxWidth: 320
                }}>
                    <div>
                        <button onClick={tryFree} disabled={busy} style={{
                            width: '100%',
                            padding: '13px 16px',
                            borderRadius: 10,
                            background: accent,
                            border: 'none',
                            color: '#fff',
                            fontSize: 15,
                            fontWeight: 600,
                            cursor: busy ? 'default' : 'pointer',
                            opacity: busy ? 0.6 : 1
                        }}>{busy ? 'Setting up…' : 'Try for free'}</button>
                        <div style={{fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 7}}>
                            An anonymous account on this device — upgrade anytime
                        </div>
                    </div>
                    <div>
                        <button onClick={login} disabled={busy} style={{
                            width: '100%',
                            padding: '13px 16px',
                            borderRadius: 10,
                            background: 'transparent',
                            border: '1px solid #30363d',
                            color: '#e6edf3',
                            fontSize: 15,
                            fontWeight: 600,
                            cursor: busy ? 'default' : 'pointer',
                            opacity: busy ? 0.6 : 1
                        }}>Log in</button>
                        <div style={{fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 7}}>
                            Use your passkey
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════════════
    // App frame
    // ═══════════════════════════════════════════════════════════════
    // The rooms panel's registered id. Named because it was spelled 'room' in both places that
    // depend on it while the registry says 'rooms' -- so "Rooms is the primary surface" never
    // fired (every visitor landed on the home grid) and the rail's pin-to-top sorted on nothing.
    const ROOMS_ID = 'rooms';

    const registry = window.PanelRegistry;
    const primaryIds = registry.filter(p => p.primary).map(p => p.id);

    // LogoutConfirm — the "log out = delete" gate for an UNIDENTIFIED user (SPEC-identity-model-v0).
    // The device key IS the identity, so logging out destroys it: rather than a top-level "delete
    // account" button, the normal Log out affordance states the consequence and requires typing
    // DELETE. An identified (passkey) user gets a benign sign-out with no DELETE gate.
    // Seats one of the site's agents in your own room (POST /rooms/agents); the server refuses anon.
    function AgentSeat() {
        const [agents, setAgents] = React.useState(null);
        const [note, setNote] = React.useState('');
        const OAuth = window.OAuthClient;
        async function open() {
            setNote('');
            try {
                const r = await OAuth.authedFetch('/rooms/agents');
                setAgents(r.ok ? ((await r.json()).agents || []) : []);
            } catch (e) { setAgents([]); setNote('Could not list agents.'); }
        }
        async function seat(name) {
            setNote('Adding ' + name + '...');
            try {
                const r = await OAuth.authedFetch('/rooms/agents', {method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({room: 'room://self', agent: name})});
                const b = await r.json().catch(() => ({}));
                setNote(r.ok ? name + ' is in your room. Address it as @' + name + '.' : (b.error || 'Refused.'));
                if (r.ok) setAgents(null);
            } catch (e) { setNote('Could not reach the server.'); }
        }
        if (!OAuth || typeof OAuth.authedFetch !== 'function') return null;
        return (
            <div className="agent-seat" style={{display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto'}}>
                {agents === null
                    ? <button className="pill" onClick={open}>Add agent</button>
                    : agents.length === 0
                        ? <span className="sub">No agents on this site.</span>
                        : agents.map(a => (
                            <button key={a.name} className="pill" onClick={() => seat(a.name)}>{a.name}</button>
                        ))}
                {note && <span className="sub">{note}</span>}
            </div>
        );
    }

    function LogoutConfirm({ onCancel, onConfirm }) {
        const [typed, setTyped] = React.useState('');
        const [busy, setBusy] = React.useState(false);
        const armed = typed.trim().toUpperCase() === 'DELETE' && !busy;
        return (
            <div style={{position:'fixed', inset:0, zIndex:60, display:'flex',
                alignItems:'center', justifyContent:'center',
                background:'rgba(0,0,0,0.6)', backdropFilter:'blur(2px)'}}>
                <div style={{width:'100%', maxWidth:420, margin:16, padding:'24px 22px',
                    background:'var(--bge, #161b22)', border:'1px solid var(--ln, #30363d)',
                    borderRadius:16, color:'var(--tx, #e6edf3)'}}>
                    <div style={{fontSize:18, fontWeight:700, marginBottom:8}}>Log out</div>
                    <div style={{fontSize:14, lineHeight:1.6, color:'var(--txm, rgba(230,237,243,0.7))'}}>
                        You're on an anonymous account that lives only on this device.
                        Logging out <b>permanently deletes it</b> — your rooms and
                        messages, everything. It can't be undone and there's no way
                        to recover it. To keep your work across devices, identify
                        instead (coming soon).
                    </div>
                    <div style={{fontSize:13, fontWeight:600, margin:'16px 0 6px'}}>
                        Type <span style={{fontFamily:'var(--mono, monospace)',
                            color:'#f85149'}}>DELETE</span> to confirm.
                    </div>
                    <input autoFocus value={typed} onChange={e => setTyped(e.target.value)}
                        placeholder="DELETE"
                        style={{width:'100%', padding:'9px 12px', fontSize:14,
                            fontFamily:'var(--mono, monospace)', color:'var(--tx, #e6edf3)',
                            background:'var(--bgi, #0d1117)', border:'1px solid var(--lns, #30363d)',
                            borderRadius:8, boxSizing:'border-box'}}/>
                    <div style={{display:'flex', gap:8, marginTop:18}}>
                        <button onClick={onCancel} disabled={busy} style={{flex:1, padding:11,
                            borderRadius:10, background:'var(--bge2, #21262d)',
                            border:'1px solid var(--ln, #30363d)', color:'var(--tx, #e6edf3)',
                            fontSize:14, cursor:'pointer'}}>Cancel</button>
                        <button onClick={() => { setBusy(true); onConfirm(); }} disabled={!armed}
                            style={{flex:1, padding:11, borderRadius:10, border:'none',
                                background:'#da3633', color:'#fff', fontSize:14, fontWeight:600,
                                opacity:armed ? 1 : 0.5, cursor:armed ? 'pointer' : 'default'}}>
                            Delete &amp; log out</button>
                    </div>
                </div>
            </div>
        );
    }

    // ─── Connection state (wt-lifecycle.mmd) → the top-bar dot ──────────────
    // "Can I see whether I am talking to the server?" is the #1 troubleshooting signal. Maps the
    // canonical FSM state bridge-op publishes to a dot colour + label, so a dropped / expired /
    // sign-in-needed channel is visible instead of a forever-green lie.
    function connMeta(fsmState) {
        switch (fsmState) {
            case 'Connected':    return { bg: 'var(--ac)', label: 'mesh', glow: true };
            case 'Connecting':
            case 'Reconnecting': return { bg: 'oklch(0.8 0.16 85)', label: 'connecting', glow: false };
            case 'Reauthing':
            case 'Expired':      return { bg: 'oklch(0.78 0.18 60)', label: 'reauthing', glow: false };
            case 'AwaitingLogin':return { bg: 'oklch(0.65 0.22 25)', label: 'sign in', glow: false };
            case 'Suspended':
            case 'Dropped':
            case 'Disconnected':
            default:             return { bg: 'var(--txd)', label: 'offline', glow: false };
        }
    }

    // useFsmConnection — the current wt-lifecycle state, updated from the 'coglative:fsm' event and
    // seeded from the live diagnostic so a late-mounting App still shows the right dot.
    function useFsmConnection() {
        const live = () => (typeof window !== 'undefined' && window.__bridgeDiag &&
            window.__bridgeDiag.bridge && window.__bridgeDiag.bridge.fsmState) || 'Disconnected';
        const [state, setState] = React.useState(live);
        React.useEffect(function () {
            function onFsm(ev) { if (ev && ev.detail && ev.detail.to) setState(ev.detail.to); }
            window.addEventListener('coglative:fsm', onFsm);
            setState(live()); // re-seed: a transition may have fired before we subscribed
            return function () { window.removeEventListener('coglative:fsm', onFsm); };
        }, []);
        return state;
    }

    function App() {
        // Rooms is the primary surface; fall back to the home grid when it is not registered.
        const [tab, setTab] = React.useState(
            () => ((window.PanelRegistry || []).some(p => p.id === ROOMS_ID) ? ROOMS_ID : 'home')
        );
        const [ctxOpen, setCtxOpen] = React.useState(false);
        const [logoutOpen, setLogoutOpen] = React.useState(false);
        const [principal, setPrincipal] = React.useState('brian');
        const [scope, setScope] = React.useState('coglative.platform');
        // identified = a passkey is bound to this account. Drives log-out semantics: identified is a
        // benign sign-out (recoverable via the passkey), unidentified is the type-DELETE destroy.
        const [identified, setIdentified] = React.useState(
            typeof localStorage !== 'undefined' && localStorage.getItem('coglative_identified') === '1'
        );
        const [identifyBusy, setIdentifyBusy] = React.useState(false);
        // The room stays mounted while the in-context login gate shows — no full-page bounce.
        const conn = useFsmConnection();
        const [loginReason, setLoginReason] = React.useState('');

        // IDENTIFY — bind a passkey onto this anonymous account's GUID. Same GUID, so the data stays
        // and the account becomes recoverable on any device; log-out stops destroying it.
        async function doIdentify() {
            if (identifyBusy) return;
            setIdentifyBusy(true);
            try {
                const { registerPasskey, passkeysAvailable } = await import('./passkey.js');
                if (!passkeysAvailable()) {
                    alert('Passkeys are not available in this browser.');
                    return;
                }
                await registerPasskey();
                localStorage.setItem('coglative_identified', '1');
                setIdentified(true);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[identify] passkey register failed:', e && e.message ? e.message : e);
                alert('Could not register a passkey: ' + (e && e.message ? e.message : e));
            } finally {
                setIdentifyBusy(false);
            }
        }

        // Benign sign-out for an IDENTIFIED account: the passkey recovers the data, so do NOT
        // cascade-delete. Drop the channel auth (in-memory token + cookie) and reload to the gate.
        // The device key may stay in IndexedDB; clearing the cookie is what shows the gate.
        async function doSignOut() {
            try {
                const session = await import('./session.js');
                const domain = /(^|\.)coglative\.com$/.test(window.location.hostname)
                    ? 'coglative.com' : undefined;
                // Also drops the local device key — fine here, since the passkey (not the device
                // key) is the recovery credential.
                await session.deleteSession(domain);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[signout] deleteSession failed:', e && e.message ? e.message : e);
            }
            // Drop the durable HUMAN cap-token, or boot would resume the just-signed-out identity.
            try {
                if (window.OAuthClient && typeof window.OAuthClient.clearCapToken === 'function') {
                    window.OAuthClient.clearCapToken();
                }
            } catch (e) { /* non-fatal */ }
            localStorage.removeItem('coglative_identified');
            window.location.reload();
        }

        // Log out = delete the unidentified identity, then reload into the gate. Signalling the
        // server to mark the account deleted immediately is task #166; today the reaper collects it.
        async function doLogout() {
            // 1. Cascade-delete over the still-authenticated channel (the server deletes the account
            //    THIS channel is authed as). Best-effort — a failure just orphans it for the reaper.
            try {
                if (window.BridgeOp && typeof window.BridgeOp.deleteAccount === 'function') {
                    await window.BridgeOp.deleteAccount();
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[logout] server deleteAccount failed:', e && e.message ? e.message : e);
            }
            // 2. Drop the local device key (the identity is gone either way).
            try {
                const session = await import('./session.js');
                const domain = /(^|\.)coglative\.com$/.test(window.location.hostname)
                    ? 'coglative.com' : undefined;
                await session.deleteSession(domain);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[logout] deleteSession failed:', e && e.message ? e.message : e);
            }
            // Clear any durable cap-token too, so reload cannot restore it.
            try {
                if (window.OAuthClient && typeof window.OAuthClient.clearCapToken === 'function') {
                    window.OAuthClient.clearCapToken();
                }
            } catch (e) { /* non-fatal */ }
            window.location.reload();
        }

        function navigate(id) {
            setTab(id);
        }

        // panel-home tiles fire bridge_mutate to intent://shell/navigate/<id>; host.js intercepts the
        // prefix and dispatches a shell:navigate CustomEvent instead of forwarding to BridgeOp. Only
        // navigate to ids the registry knows, so a stale tile cannot strand the tab on a dead value.
        React.useEffect(function () {
            function onShellNav(ev) {
                const target = ev && ev.detail;
                if (typeof target !== 'string' || !target) return;
                if (target === 'home' || registry.some(function (p) { return p.id === target; })) {
                    setTab(target);
                }
            }
            window.addEventListener('shell:navigate', onShellNav);
            return function () { window.removeEventListener('shell:navigate', onShellNav); };
        }, []);

        // Wire bridge-op's credential lifecycle to the UI: onNeedsLogin captures the reason for the
        // banner, whose VISIBILITY is driven off conn === 'AwaitingLogin' (the FSM is the single
        // source of truth), so it auto-hides on recovery.
        React.useEffect(function () {
            if (!window.BridgeOp || typeof window.BridgeOp.onNeedsLogin !== 'function') return;
            window.BridgeOp.onNeedsLogin(function (reason) { setLoginReason(reason || ''); });
            return function () {
                if (window.BridgeOp && typeof window.BridgeOp.onNeedsLogin === 'function')
                    window.BridgeOp.onNeedsLogin(null);
            };
        }, []);

        // Sign-in from the in-context gate. signIn() hard-redirects and the callback reloads into a
        // clean connect, so loginOk() may never be reached — the fresh boot re-establishes Connected.
        async function doRelogin() {
            // OAuth/human user (holds a cap-token): re-present the IdP.
            if (window.OAuthClient && typeof window.OAuthClient.isAuthenticated === 'function'
                && window.OAuthClient.isAuthenticated()) {
                try { await window.OAuthClient.signIn(); }
                catch (e) {
                    // eslint-disable-next-line no-console
                    console.error('[relogin] signIn failed:', e && e.message ? e.message : e);
                }
                return;
            }
            // A passkey holds this device key's account: the passkey, not a new key, is the way back.
            // Rotating here would lose nothing on the server but would leave the human in a new empty account.
            const { reloginAction } = await import('./session.js');
            const action = reloginAction(loginReason, localStorage.getItem('coglative_identified') === '1');
            if (action === 'passkey') {
                try {
                    const { loginPasskey } = await import('./passkey.js');
                    await loginPasskey();
                    localStorage.setItem('coglative_identified', '1');
                    if (window.BridgeOp && typeof window.BridgeOp.loginOk === 'function') window.BridgeOp.loginOk();
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error('[relogin] passkey login failed:', e && e.message ? e.message : e);
                }
                return;
            }
            // Anon with no passkey: the device key is the only way into the account, so it is deleted only for
            // a revoked subject (models/logout_reentry.qnt noAccountLost). Any other reason re-signs with the
            // same key; a revoked answer to that brings the gate back with 'revoked'. Bouncing an anon user to
            // an IdP they have no account for would strand them (adversarial security finding).
            try {
                const session = await import('./session.js');
                const domain = /(^|\.)coglative\.com$/.test(window.location.hostname)
                    ? 'coglative.com' : undefined;
                if (action === 'rotate') {
                    await session.deleteSession(domain);             // drop the revoked device key
                    await session.ensureSession(undefined, domain);  // mint a fresh GUID + token
                } else {
                    await session.resignSession(undefined, domain);
                }
                if (window.BridgeOp && typeof window.BridgeOp.loginOk === 'function') window.BridgeOp.loginOk();
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[relogin] anon re-establish failed:', e && e.message ? e.message : e);
            }
        }
        // Dismiss: AwaitingLogin -> Disconnected; the banner hides as conn leaves AwaitingLogin.
        function dismissLogin() {
            try {
                if (window.BridgeOp && typeof window.BridgeOp.loginAbandoned === 'function')
                    window.BridgeOp.loginAbandoned();
            } catch (_) { /* non-fatal */ }
        }

        function applyCtx(p, s) {
            setPrincipal(p);
            setScope(s);
        }

        const current = registry.find(p => p.id === tab) || registry[0];
        const isSecondary = !primaryIds.includes(tab) && tab !== 'home' && tab !== 'more';
        const cm = connMeta(conn);

        return (
            <div className="shell">
                {/* Top bar */}
                <div className="bar">
                    <div className="mark"><i>◆</i> Coglative</div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0}}>
                        <button className="pill" onClick={() => setCtxOpen(true)}>
                            <span style={{width: 7, height: 7, borderRadius: '50%', background: 'var(--hu)'}}/>
                            {principal}
                            <span style={{color: 'var(--txd)'}}>▾</span>
                        </button>
                        <span style={{color: 'var(--txd)', fontFamily: 'var(--mono)', fontSize: 11}}>on</span>
                        <button className="pill" onClick={() => setCtxOpen(true)}>
                            <span style={{
                                color: 'var(--txm)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>{scope}</span>
                            <span style={{color: 'var(--txd)'}}>▾</span>
                        </button>
                    </div>
                    <div title={'connection: ' + conn} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontFamily: 'var(--mono)',
                        fontSize: 10,
                        color: 'var(--txd)'
                    }}>
                        <span style={{
                            width: 6,
                            height: 6,
                            borderRadius: 3,
                            background: cm.bg,
                            boxShadow: cm.glow ? '0 0 0 3px oklch(0.82 0.17 142/0.2)' : 'none',
                            transition: 'background 0.3s ease'
                        }}/>
                        {cm.label}
                    </div>
                </div>

                {/* In-context credential gate (wt-lifecycle AwaitingLogin). The
                    room stays mounted behind it — Slack-parity recovery, no
                    full-page bounce. Visibility is driven off the FSM state, so
                    it auto-hides the instant the channel recovers. */}
                {conn === 'AwaitingLogin' && (
                    <div role="alert" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 16px',
                        background: 'oklch(0.32 0.09 25)',
                        color: 'var(--tx)',
                        fontSize: 13,
                        borderBottom: '1px solid oklch(0.65 0.22 25/0.45)'
                    }}>
                        <span style={{flex: 1, minWidth: 0}}>
                            {loginReason === 'passkey_required'
                                ? 'This account has a passkey. Sign in with it to continue.'
                                : loginReason === 'revoked'
                                ? 'Your session was signed out elsewhere. Sign in to continue — your room stays open.'
                                : 'Your session expired. Sign in to continue — your room stays open.'}
                        </span>
                        <button className="pill" onClick={doRelogin}>Sign in</button>
                        <button className="pill" onClick={dismissLogin} style={{opacity: 0.7}}>Dismiss</button>
                    </div>
                )}

                <div className="body">
                    {/* Desktop nav rail — Rooms pinned to the top as the primary
                        surface; the rest keep discovery order. */}
                    <nav className="rail">
                        {registry.slice().sort((a, b) => (b.id === ROOMS_ID) - (a.id === ROOMS_ID)).map(p => (
                            <button key={p.id} className={tab === p.id ? 'on' : ''} onClick={() => navigate(p.id)}
                                    title={p.title}>
                                {ICONS[p.icon]}
                                {p.badge && <span className="b">{p.badge}</span>}
                            </button>
                        ))}
                        <div className="sp"/>
                        {!identified && (
                            <button title="Identify — add a passkey to keep your work across devices"
                                    onClick={doIdentify} disabled={identifyBusy}
                                    style={{opacity: identifyBusy ? 0.5 : 1}}>{ICONS.iam}</button>
                        )}
                        <button title={identified ? 'Sign out' : 'Log out'}
                                onClick={() => identified ? doSignOut() : setLogoutOpen(true)}>{ICONS.settings}</button>
                    </nav>

                    {/* Panel pane */}
                    <div className="pane">
                        {/* Frame-owned header for non-home panels */}
                        {tab !== 'home' && (
                            <div className="pane-head">
                                {isSecondary && <button onClick={() => setTab('home')} style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: 'var(--txm)',
                                    display: 'flex',
                                    padding: 4
                                }}>{ICONS.back}</button>}
                                <div className="title">{current.title}</div>
                                <div className="sub">{current.id}</div>
                                {identified && current.id === ROOMS_ID && <AgentSeat/>}
                            </div>
                        )}
                        <div className="pane-body">
                            {/* Mount `current` (the resolved panel), NOT the raw
                                `tab`: on boot tab==='home' but there is no 'home'
                                panel, so render the first available panel ('your
                                data') instead of stranding on the empty state.
                                Only when the registry is truly empty do we fall
                                back to `tab` (→ the real empty state). */}
                            <LazyPanel id={current ? current.id : tab} principal={principal}
                                scope={scope} onNavigate={navigate}/>
                        </div>
                    </div>
                </div>

                {/* Mobile bottom tabs */}
                <div className="btab">
                    <div className="btab-inner">
                        {[...primaryIds.slice(0, 4), 'more'].map(id => {
                            const p = registry.find(r => r.id === id) || {id: 'more', title: 'More', icon: 'more'};
                            const active = id === 'more' ? (!primaryIds.includes(tab) && tab !== 'home') : tab === id;
                            return (
                                <button key={id} className={active ? 'on' : ''}
                                        onClick={() => navigate(id === 'more' ? 'home' : id)}>
                                    {ICONS[p.icon]}
                                    <span>{p.title}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <ContextModal open={ctxOpen} onClose={() => setCtxOpen(false)} onApply={applyCtx}/>
                {logoutOpen && <LogoutConfirm onCancel={() => setLogoutOpen(false)} onConfirm={doLogout}/>}
            </div>
        );
    }

    // ensureAnonSession — establish the anonymous EBA (device-key) session: the self-signed token
    // publishes window.__coglativeAnonToken plus the cookie the WT CONNECT carries. The device key IS
    // the identity. Idempotent. It does NOT auto-run at boot — only after the gate decision or for an
    // already-anon returning visitor, because setting the cookie earlier would suppress the gate.
    async function ensureAnonSession() {
        try {
            const session = await import('./session.js');
            const domain = /(^|\.)coglative\.com$/.test(window.location.hostname)
                ? 'coglative.com' : undefined;
            await session.ensureSession(undefined, domain);
        } catch (e) {
            // EBA bootstrap failed (no crypto/IndexedDB). Render the shell anyway — panels degrade
            // to fixtures rather than stranding the user on a blank screen.
            // eslint-disable-next-line no-console
            console.error('[boot] anon EBA session failed:', e && e.message ? e.message : e);
        }
    }

    // Publish the device-key re-sign for credential-refresh.js, which reads
    // window.__coglativeEnsureSession as its LAST-RESORT lane and, until now,
    // found nothing there. Both readers (credential-refresh.js, bridge-op.js)
    // existed with no writer, so the lane silently no-opped: the refresher fell
    // through to needsLogin, the FSM parked in AwaitingLogin, and dominance
    // vetoed every later recovery -- terminal without a human, on a device whose
    // key could have re-signed offline the whole time.
    if (typeof window !== 'undefined') {
        window.__coglativeEnsureSession = ensureAnonSession;
    }

    // session_hasDeviceKey — the DURABLE returning-visitor witness (the cookie below only says "a
    // session was recently established"). Read-only, so the question cannot create its own answer.
    async function session_hasDeviceKey() {
        try {
            const session = await import('./session.js');
            return await session.hasDeviceKey();
        } catch (_) {
            return false; // storage/crypto unavailable -> fall back to the gate
        }
    }

    // hasExistingAnonSession — a non-empty session cookie means this device entered anonymously on a
    // prior visit, so it skips the gate.
    function hasExistingAnonSession() {
        const m = /(?:^|;\s*)coglative_session_capability=([^;]+)/.exec(document.cookie || '');
        return !!(m && m[1] && m[1].length > 0);
    }

    // ONE React root for #root, lazily created. Boot may render the Gate and then the App; both go
    // through this root, because createRoot() twice on one container is a React-18 error.
    let _appRoot = null;
    function appRoot() {
        if (!_appRoot) _appRoot = createRoot(document.getElementById('root'));
        return _appRoot;
    }

    // bootApp — the post-auth body: merge discovered panels into the registry, then mount React.
    // Reached only once we hold a principal.
    // Boot phases are timed and published on window.__bootPhases. The panel-load path reports
    // itself (window.__panelLoadPhases) and totals ~1.2s, against a first paint 10x that -- so the
    // time is BEFORE the load effect and nothing measured it. resource timing cannot see it either:
    // it excludes WebTransport, so a boot waiting on the bridge reads as an idle network.
    // LONG-TASK CAPTURE, armed as early as page code runs. The main thread blocks for seconds
    // after the panel load completes: a Runtime.evaluate injected from outside could not execute
    // until 11392ms, and a 250ms poll loop reported "painted" at 16188ms for children that
    // existed at ~4820ms. Neither is a measurement of the page -- both are measurements of being
    // BLOCKED. An observer installed by the page itself is the only thing that sees the task that
    // did the blocking, and attribution names the frame it came from.
    try {
        window.__longtasks = [];
        new PerformanceObserver(function (list) {
            for (const e of list.getEntries()) {
                if (window.__longtasks.length >= 200) return;
                window.__longtasks.push({
                    start: Math.round(e.startTime),
                    dur: Math.round(e.duration),
                    name: e.name,
                    attr: (e.attribution || []).map(function (a) { return a.name; }).join(','),
                });
            }
        }).observe({ entryTypes: ['longtask'] });
    } catch (_) { /* not supported: the rest of boot is unaffected */ }

    // PER RUN: bootApp executes TWICE per page load (measured on the DC), so a single record
    // reports the second, warm pass and hides the first. __bootPhases stays pointing at the
    // FIRST run, which is the expensive one.
    function _bootMark(name, from, runIdx) {
        try {
            const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
            window.__bootRuns[runIdx][name] = Math.round(now - from);
            window.__bootPhases = window.__bootRuns[0];
        } catch (_) { /* instrumentation must never break boot */ }
        return (typeof performance !== 'undefined' ? performance.now() : Date.now());
    }

    // Merge discovered panels over the static registry; true when it changed anything.
    function mergeDiscovered(discovered) {
        if (!Array.isArray(discovered) || discovered.length === 0) return false;
        window.PanelRegistry = window.PanelRegistry || [];
        const idIndex = new Map();
        window.PanelRegistry.forEach((p, i) => idIndex.set(p.id, i));
        let overrides = 0;
        let appends = 0;
        for (const entry of discovered) {
            const at = idIndex.get(entry.id);
            if (at !== undefined) {
                // Preserve `primary` from the static entry — discovered manifests do not yet
                // declare tab-bar placement.
                const wasPrimary = window.PanelRegistry[at].primary;
                window.PanelRegistry[at] = wasPrimary
                    ? Object.assign({}, entry, { primary: true })
                    : entry;
                overrides++;
            } else {
                window.PanelRegistry.push(entry);
                appends++;
            }
        }
        // eslint-disable-next-line no-console
        console.log('[boot] panel-discovery:', overrides,
            'override(s),', appends, 'append(s) from substrate');
        return true;
    }

    // Discover again and re-render with what arrived, so a chrome that booted on an empty or refused registry
    // leaves "No panel registered" without a reload (models/wt_web_discovery.qnt notified).
    async function rediscover() {
        const discovered = await discoverPanels({ bundleBaseUrl: window.location.origin });
        if (mergeDiscovered(discovered)) appRoot().render(<App/>);
    }

    // A refused discovery is transient: retry it with backoff, without a bound.
    function retryDiscovery(delayMs) {
        setTimeout(function () {
            rediscover().catch(function (e) {
                if (e && e.refused) retryDiscovery(Math.min(30000, delayMs * 2));
            });
        }, delayMs);
    }

    // The server pushes a SET_SHIFTED on the held panel subscription when its registry changes.
    function watchPanelRegistry() {
        if (window.__panelRegistryWatched || !window.BridgeOp
            || typeof window.BridgeOp.onSubscriptionEvent !== 'function') return;
        window.__panelRegistryWatched = true;
        window.BridgeOp.onSubscriptionEvent(function (ev) {
            const uri = ev && ev.predicateResult && ev.predicateResult.predicateUri;
            if (ev && ev.eventType === 3 && uri === PANEL_PREDICATE_URI) {
                rediscover().catch(function (e) { if (e && e.refused) retryDiscovery(1000); });
            }
        });
    }

    async function bootApp() {
        let _bt = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        const _bt0 = _bt;
        const _runIdx = (window.__bootRuns = window.__bootRuns || []).length;
        window.__bootRuns.push({ bootAppStartedAtMs: Math.round(_bt0) });
        window.__bootPhases = window.__bootRuns[0];
        // Expose the anon EBA re-sign so bridge-op (a classic-script IIFE that cannot import
        // session.js) can refresh the credential BEFORE EVERY dial — the fix for the
        // reconnect-on-expired-token death loop (docs/BRIDGE-PROTOCOL.md §10.5). OAuth skips it.
        // It is ALSO credential-refresh.js's anon lane, so publish it before the install() below.
        try {
            const session = await import('./session.js');
            const domain = /(^|\.)coglative\.com$/.test(window.location.hostname)
                ? 'coglative.com' : undefined;
            window.__coglativeEnsureSession = function () {
                return session.ensureSession(undefined, domain);
            };
            // This device's own token as the device cookie on every dial — independent of the
            // bearer, so an identified session still keys its OWN device cell (#131).
            window.__coglativeEnsureDeviceCookie = function () {
                return session.ensureDeviceCookie(undefined, domain);
            };
        } catch (e) { /* non-fatal: bridge-op keeps using the boot token */ }
        // The credential refresher lives in credential-refresh.js, which registers itself at document
        // parse (right after bridge-op.js) — deliberately NOT here, because a subscribe can fail on the
        // way in before this React boot ever runs. This install() is idempotent belt-and-braces for a
        // boot order where BridgeOp mounted late. It is NOT a second implementation: one refresher,
        // one place, so the passkey / OAuth / anon lanes cannot drift apart.
        try {
            if (window.CredentialRefresh && typeof window.CredentialRefresh.install === 'function') {
                window.CredentialRefresh.install();
            }
        } catch (e) { /* non-fatal: bridge-op parks in Expired and retries, never logs the human out */ }
        try {
            // Bundles are fetched same-origin (the wt-server fronts the deployment's bundle store)
            // rather than a hardcoded CDN, so one host serves chrome + bundles in every deployment
            // class. Bytes stay hash-pinned + signature-checked.
            _bt = _bootMark('preDiscovery', _bt, _runIdx);
            const discovered = await discoverPanels({ bundleBaseUrl: window.location.origin });
            _bt = _bootMark('discoverPanels', _bt, _runIdx);
            mergeDiscovered(discovered);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[boot] panel-discovery failed (rendering with static registry):',
                e && e.message ? e.message : e);
            if (e && e.refused) retryDiscovery(1000);
        }
        watchPanelRegistry();
        _bt = _bootMark('registryMerge', _bt, _runIdx);
        appRoot().render(<App/>);
        _bootMark('reactRender', _bt, _runIdx);
        try {
            window.__bootRuns[_runIdx].totalMs = Math.round(
                (typeof performance !== 'undefined' ? performance.now() : Date.now()) - _bt0);
            console.log('[boot] phases run=' + _runIdx + ' '
                + JSON.stringify(window.__bootRuns[_runIdx]));
        } catch (_) {}
        // Web Push: register the SW, subscribe, and hand the endpoint to the substrate so a closed
        // tab is still reachable out-of-band. Fire-and-forget; never blocks UI.
        setupWebPush();
    }

    // setupWebPush — register the service worker, request permission, subscribe with the server's
    // VAPID key, and hand the subscription up via BridgeOp.registerPush. Idempotent (reuses an
    // existing subscription); every failure is swallowed — push is an enhancement, not a requirement.
    async function setupWebPush() {
        try {
            if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;
            const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
            const perm = await Notification.requestPermission();
            if (perm !== 'granted') { console.log('[push] permission not granted:', perm); return; }
            const resp = await fetch('/.well-known/vapid.json');
            const vk = await resp.json();
            if (!vk || !vk.key) { console.warn('[push] no VAPID key from server'); return; }
            const appKey = urlBase64ToUint8Array(vk.key);
            let sub = await reg.pushManager.getSubscription();
            if (!sub) {
                sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
            }
            const j = sub.toJSON();
            if (j && j.endpoint && j.keys && j.keys.p256dh && j.keys.auth &&
                window.BridgeOp && typeof window.BridgeOp.registerPush === 'function') {
                await window.BridgeOp.registerPush(j.endpoint, j.keys.p256dh, j.keys.auth);
                console.log('[push] registered web push subscription with substrate');
            }
        } catch (e) {
            console.warn('[push] setup failed:', e && e.message ? e.message : e);
        }
    }

    // base64url (the VAPID applicationServerKey format) → Uint8Array.
    function urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const raw = atob(base64);
        const out = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
        return out;
    }

    (async function bootWithDiscovery() {
        const OAuth = window.OAuthClient;

        // Complete an in-flight OAuth callback if present (identify path).
        if (OAuth && OAuth.looksLikeCallback()) {
            try {
                await OAuth.handleCallback();
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('[boot] OAuth callback failed:',
                    e && e.message ? e.message : e);
                // fall through; the visitor lands back on the gate
            }
        }

        let identified = !!(OAuth && OAuth.isCapTokenLikelyValid());

        // A cap-token is 15 MINUTES, so restoreCapToken() drops it on any reload past that and the
        // human fell through to the anon device-key lane -- a different principal, whose room://self
        // is a different, empty room. The 7-day refresh grant e810feb minted was sitting in storage
        // with nothing that spends it at boot. {failed} is a blip, NOT evidence about this human, so
        // it keeps the identified session rather than demoting to anon.
        if (!identified && OAuth && typeof OAuth.hasRefreshToken === 'function' && OAuth.hasRefreshToken()) {
            try {
                const r = await OAuth.refreshWithRefreshToken();
                if (r && r.ok) identified = true;
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('[boot] refresh-grant failed:', e && e.message ? e.message : e);
            }
        }

        // A PERSISTED DEVICE KEY MEANS THIS IS NOT A FRESH VISITOR. "Fresh" used to mean "no valid
        // cap-token and no session COOKIE", and the cookie is the wrong witness: it expires, the
        // device key does not. A returning user whose cookie lapsed was shown the paywall as a
        // stranger while the key that mints this device's anon identity sat in IndexedDB untouched.
        // Consulting it does not weaken the gate: it is per-device, created only by a prior
        // deliberate entry, and hasDeviceKey() is read-only, so asking cannot mint its own evidence.
        const returningDevice = await session_hasDeviceKey();
        if (!identified && !hasExistingAnonSession() && !returningDevice) {
            appRoot().render(
                <Gate
                    onLogin={async () => {
                        // Passkey login: the server mints a HUMAN cap-token (adopted as the WT
                        // Bearer) and sets the session cookie. No OAuth redirect — passkeys only.
                        const { loginPasskey } = await import('./passkey.js');
                        try {
                            await loginPasskey();
                            localStorage.setItem('coglative_identified', '1');
                        } catch (e) {
                            // Passkey login failed (e.g. the credential store was lost). Do NOT
                            // strand the user at the Gate — fall through to the anon device-key
                            // session; a passkey can be re-registered later.
                            console.warn('[gate] passkey login failed; continuing as anon:',
                                e && e.message ? e.message : e);
                            await ensureAnonSession();
                        }
                        await bootApp();
                    }}
                    onTryFree={async () => { await ensureAnonSession(); await bootApp(); }}
                />
            );
            return;
        }

        // Returning or identified: (re)ensure the anon session so token + cookie are fresh, then boot.
        if (!identified) await ensureAnonSession();
        await bootApp();
    })();
